import React from 'react';

/**
 * Componente de Calendário Visual do ZARC
 * Mostra os meses do ano coloridos de acordo com os decêndios ideais
 * 
 * @param {string} decendiosIdeais - String com decêndios separados por vírgula (ex: "1,2,3,10,11")
 * @param {string} decendiosAdequados - String com decêndios adequados
 * @param {string} decendiosAtencao - String com decêndios de atenção
 * @param {boolean} compact - Modo compacto (padrão: false)
 */
function CalendarioZARC({ 
  decendiosIdeais = '', 
  decendiosAdequados = '', 
  decendiosAtencao = '',
  compact = false 
}) {
  
  // Converter strings em arrays de números
  const parseDecendios = (str) => {
    if (!str || str === '') return [];
    return str.split(',').map(Number).filter(n => !isNaN(n));
  };
  
  const ideais = parseDecendios(decendiosIdeais);
  const adequados = parseDecendios(decendiosAdequados);
  const atencao = parseDecendios(decendiosAtencao);
  
  // Configuração dos meses
  const meses = [
    { nome: 'Jan', decendios: [1, 2, 3] },
    { nome: 'Fev', decendios: [4, 5, 6] },
    { nome: 'Mar', decendios: [7, 8, 9] },
    { nome: 'Abr', decendios: [10, 11, 12] },
    { nome: 'Mai', decendios: [13, 14, 15] },
    { nome: 'Jun', decendios: [16, 17, 18] },
    { nome: 'Jul', decendios: [19, 20, 21] },
    { nome: 'Ago', decendios: [22, 23, 24] },
    { nome: 'Set', decendios: [25, 26, 27] },
    { nome: 'Out', decendios: [28, 29, 30] },
    { nome: 'Nov', decendios: [31, 32, 33] },
    { nome: 'Dez', decendios: [34, 35, 36] }
  ];
  
  // Calcular cor e informações de cada mês
  const calcularInfoMes = (mes) => {
    const contadores = {
      ideais: mes.decendios.filter(d => ideais.includes(d)).length,
      adequados: mes.decendios.filter(d => adequados.includes(d)).length,
      atencao: mes.decendios.filter(d => atencao.includes(d)).length
    };
    
    // Prioridade: ideais > adequados > atenção
    let cor, texto, nivel;
    
    if (contadores.ideais === 3) {
      cor = '#4caf50';  // Verde escuro - todos ideais
      texto = '✓';
      nivel = 'ideal';
    } else if (contadores.ideais >= 2) {
      cor = '#8bc34a';  // Verde claro - maioria ideal
      texto = '✓';
      nivel = 'bom';
    } else if (contadores.ideais >= 1 || contadores.adequados >= 2) {
      cor = '#ffc107';  // Amarelo - adequado
      texto = '~';
      nivel = 'adequado';
    } else if (contadores.adequados >= 1 || contadores.atencao >= 1) {
      cor = '#ff9800';  // Laranja - atenção
      texto = '!';
      nivel = 'atencao';
    } else {
      cor = '#bdbdbd';  // Cinza - não recomendado
      texto = '✗';
      nivel = 'nao_recomendado';
    }
    
    return { cor, texto, nivel, contadores };
  };
  
  const isMobile = window.innerWidth <= 768;
  
  return (
    <div>
      {/* Calendário */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: compact ? 'repeat(6, 1fr)' : isMobile ? 'repeat(4, 1fr)' : 'repeat(6, 1fr)',
        gap: compact ? 4 : isMobile ? 6 : 8,
        marginBottom: compact ? 8 : 12
      }}>
        {meses.map(mes => {
          const info = calcularInfoMes(mes);
          
          return (
            <div
              key={mes.nome}
              title={`${mes.nome}: ${info.contadores.ideais} ideais, ${info.contadores.adequados} adequados, ${info.contadores.atencao} atenção`}
              style={{
                background: info.cor,
                color: 'white',
                padding: compact ? 6 : isMobile ? 8 : 10,
                borderRadius: compact ? 4 : 6,
                textAlign: 'center',
                fontSize: compact ? 10 : isMobile ? 11 : 12,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              }}
            >
              <div>{mes.nome}</div>
              {!compact && (
                <div style={{ fontSize: 14, marginTop: 2 }}>
                  {info.texto}
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Legenda */}
      {!compact && (
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: isMobile ? 6 : 8,
          fontSize: isMobile ? 10 : 11,
          marginTop: 8
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{
              width: 12,
              height: 12,
              backgroundColor: '#4caf50',
              borderRadius: 2
            }} />
            <span>Ideal</span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{
              width: 12,
              height: 12,
              backgroundColor: '#8bc34a',
              borderRadius: 2
            }} />
            <span>Bom</span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{
              width: 12,
              height: 12,
              backgroundColor: '#ffc107',
              borderRadius: 2
            }} />
            <span>Adequado</span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{
              width: 12,
              height: 12,
              backgroundColor: '#ff9800',
              borderRadius: 2
            }} />
            <span>Atenção</span>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{
              width: 12,
              height: 12,
              backgroundColor: '#bdbdbd',
              borderRadius: 2
            }} />
            <span>Não recomendado</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendarioZARC;
